import { useModal, create, show } from "@ebay/nice-modal-react";

import useTypedContext from "hooks/useTypedContext";
import FlashContext from "components/FlashMessages/FlashContext";
import DeleteConfirmationModal from "ds/components/DeleteConfirmationModal";
import { ConfirmationModalMessage } from "ds/components/ConfirmationModal/Message";
import useApiKeyRoleDelete from "shared/Role/useDeleteApiKeyRole";

import { UserManagementActivationStatus } from "../../types";

type UnassignConfirmationProps = {
  id: string;
  name: string;
  refetchQueries: string[];
  activationStatus: UserManagementActivationStatus;
  isLastRole?: boolean;
};

const UnassignConfirmation = create(function UnassignConfirmation({
  id,
  name,
  refetchQueries,
  activationStatus,
  isLastRole = false,
}: UnassignConfirmationProps) {
  const { onError, reportSuccess } = useTypedContext(FlashContext);

  const modal = useModal();

  const [deleteApiKeyRoleBinding, { loading, data }] = useApiKeyRoleDelete({
    refetchQueries,
    awaitRefetchQueries: true,
  });

  const onDelete = () => {
    deleteApiKeyRoleBinding({ id })
      .then((result) => {
        if (result?.data?.apiKeyRoleBindingDelete?.id) {
          reportSuccess({ message: `${name} role successfully unassigned` });
          modal.resolve();
          modal.hide();
        }
      })
      .catch(onError);
  };

  const handleOnConfirm = () => {
    onDelete();
  };

  return (
    <DeleteConfirmationModal
      isLoading={loading}
      isDismissable={!loading && !data?.apiKeyRoleBindingDelete?.id}
      title="Unassign role"
      confirmationButtonLabel="Unassign"
      onConfirm={handleOnConfirm}
    >
      <ConfirmationModalMessage>
        {isLastRole && activationStatus === UserManagementActivationStatus.ACTIVE ? (
          <>
            Unassigning <strong>{name}</strong> will revoke the API key's access to Spacelift
            resources.
            <br />
            <br />
            Are you sure you want to unassign this role?
          </>
        ) : (
          <>
            Are you sure you want to unassign the role <strong>{name}</strong>? You cannot undo this
            action.
          </>
        )}
      </ConfirmationModalMessage>
    </DeleteConfirmationModal>
  );
});

export const showUnassignConfirmation = (props: UnassignConfirmationProps) =>
  show(UnassignConfirmation, props);
